// AuthContext.jsx
import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import AuthService from './AuthService'

// Create the authentication context
const AuthContext = createContext(null)

// Create a provider component for the authentication context
export function AuthProvider ({ children }) {
  const [isLoading, setIsLoading] = useState(true)
  const [authState, setAuthState] = useState(false)
  const auth = useMemo(() => new AuthService(), [])

  // Check initial auth state
  useEffect(() => {
    const checkAuth = () => {
      const isUserLoggedIn = auth.loggedIn()
      setAuthState(isUserLoggedIn)
      setIsLoading(false)
    }

    checkAuth()

    // Set up an interval to periodically check token expiration
    const interval = setInterval(checkAuth, 30000) // Check every 30 seconds

    return () => clearInterval(interval)
  }, [auth])

  // Wrap the login method with navigation
  const login = useCallback(
    async (email, password) => {
      try {
        await auth.login(email, password)
        setAuthState(true)
        return true
      } catch (error) {
        console.error('Login error:', error)
        return false
      }
    },
    [auth]
  )

  // Wrap the logout method
  const logout = useCallback(() => {
    auth.logout()
    setAuthState(false)
  }, [auth])

  // Check if the user is authenticated
  const isAuthenticated = useCallback(() => {
    const loggedIn = auth.loggedIn()
    if (authState !== loggedIn) {
      setAuthState(loggedIn)
    }
    return loggedIn
  }, [auth, authState])

  // Provide the authentication context value
  const value = useMemo(
    () => ({
      auth,
      login,
      logout,
      isAuthenticated,
      isLoading,
    }),
    [auth, login, logout, isAuthenticated, isLoading]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

// Create a hook to use the authentication context
export function useAuth () {
  const context = useContext(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  // Get navigate function if we're in a router context
  const navigate = useNavigate()
  const location = useLocation()

  // Enhanced login function with navigation
  const enhancedLogin = useCallback(
    async (email, password) => {
      const success = await context.login(email, password)
      if (success) {
        const from = location.state?.from?.pathname || '/home'
        navigate(from, { replace: true })
      }
      return success
    },
    [context, navigate, location]
  )

  // Enhanced logout function with navigation
  const enhancedLogout = useCallback(() => {
    context.logout()
    navigate('/login')
  }, [context, navigate])

  // Return the context with the enhanced login/logout
  return {
    ...context,
    login: enhancedLogin,
    logout: enhancedLogout,
  }
}

// Create a protected route component
export function RequireAuth ({ children }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Show loading state
  if (isLoading) {
    return <div>Loading authentication status...</div>
  }

  if (!isAuthenticated()) {
    // Redirect to the login page with a return url
    return <Navigate to='/login' state={{ from: location }} replace />
  }

  return children
}

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired,
}
